import { Navigate, RouteObject, useMatches as baseUseMatches } from 'react-router-dom';

import { ErrorElement } from '~/pages/ErrorPage/index';
import { ReportsPage } from './pages/Reports';
import { IconType, ClipboardOutlineIcon, BarGraphFillIcon, SuitcaseFillIcon, DocumentCheckOutlineIcon } from './icons';
import { ApplicationsPage } from './pages/Applications';
import { ApplicationDetailsPage } from './pages/ApplicationDetails';
import { IntakeManagementPage } from './pages/IntakeManagement';
import { IntakeDetailsPage } from './pages/IntakeDetails';
import { IntakeCreatePage } from './pages/IntakeCreate';
import { ProgramCatalogPage } from './pages/ProgramCatalog';
import { ApplicationExportPage } from './pages/ApplicationExport';
import { BookOutlineIcon, DocumentSyncOutlineIcon } from '@applyboard/ui-icons';
import { ProgramEditPage } from './pages/ProgramEdit';
import { ProgramDetailsPage } from './pages/ProgramDetails';
import { CourseMappingsPage } from './pages/CourseMappings';
import { GradeConversionPage } from './pages/GradeConversion';
import { ConversionRulesPage } from './pages/ConversionRules/ConversionRulesPage';
import { GradeConversionDetailsPage } from './pages/GradeConversionDetails/GradeConversionDetailsPage';
import { GradeConversionCreatePage } from './pages/GradeConversionCreate';

export type Route = {
  name: string;
  path: string;
  crumb?: string;
  icon: IconType;
  hidden?: boolean;
  children?: Route[];
  page: React.ReactNode;
  /**
   * When true, children render inside this route's layout via <Outlet />.
   * When false (default), children render as separate pages, replacing the parent.
   */
  nestedLayout?: boolean;
};

export const routes: Route[] = [
  {
    name: 'Applications',
    crumb: 'Applications',
    icon: ClipboardOutlineIcon,
    path: '/applications',
    page: <ApplicationsPage />,
    children: [
      {
        name: 'Application Details',
        crumb: 'Application Details',
        path: ':id',
        icon: ClipboardOutlineIcon,
        page: <ApplicationDetailsPage />,
        children: [
          {
            name: 'Change Primary Program Intake',
            crumb: 'Change Primary Program Intake',
            path: 'program-edit',
            icon: ClipboardOutlineIcon,
            page: <ProgramEditPage />,
          },
        ],
      },
    ],
  },
  {
    name: 'Application Reports',
    crumb: 'Application Reports',
    icon: BarGraphFillIcon,
    path: '/reports',
    page: <ReportsPage />,
  },
  {
    name: 'Program Catalog',
    crumb: 'Program Catalog',
    icon: BookOutlineIcon,
    path: '/program-catalog',
    page: <ProgramCatalogPage />,
    children: [
      {
        name: 'Program Details',
        crumb: 'Program Details',
        path: ':id',
        icon: BookOutlineIcon,
        page: <ProgramDetailsPage />,
      },
    ],
  },
  {
    name: 'Intake Management',
    crumb: 'Intake Management',
    icon: SuitcaseFillIcon,
    path: '/intake-management',
    page: <IntakeManagementPage />,
    children: [
      {
        name: 'Add New Intake',
        crumb: 'Add New Intake',
        path: 'new',
        icon: SuitcaseFillIcon,
        page: <IntakeCreatePage />,
      },
      {
        name: 'Program Intake',
        crumb: 'Program Intake',
        path: ':id',
        icon: SuitcaseFillIcon,
        page: <IntakeDetailsPage />,
      },
    ],
  },
  {
    name: 'Data Export',
    crumb: 'Data Export',
    icon: DocumentCheckOutlineIcon,
    path: '/application-export',
    page: <ApplicationExportPage />,
  },
  {
    name: 'Conversion Rules',
    crumb: 'Conversion Rules',
    icon: DocumentSyncOutlineIcon,
    path: '/conversion-rules',
    page: <ConversionRulesPage />,
    hidden: true,
    nestedLayout: true,
    children: [
      {
        name: 'Conversion Rules Redirect',
        crumb: 'Conversion Rules',
        path: '',
        icon: DocumentSyncOutlineIcon,
        page: <Navigate to="course-mappings" replace />,
        hidden: true,
      },
      {
        name: 'Course Mappings',
        crumb: 'Course Mappings',
        path: 'course-mappings',
        icon: DocumentSyncOutlineIcon,
        page: <CourseMappingsPage />,
      },
      {
        name: 'Grade Conversions',
        crumb: 'Grade Conversions',
        path: 'grade-conversions',
        icon: DocumentSyncOutlineIcon,
        page: <GradeConversionPage />,
      },
    ],
  },
  {
    name: 'Grade Conversion Details',
    crumb: 'Grade Conversion Details',
    path: '/conversion-rules/grade-conversions/:id',
    icon: DocumentSyncOutlineIcon,
    page: <GradeConversionDetailsPage />,
    hidden: true,
  },
  {
    name: 'Create Grade Conversion',
    crumb: 'Create Grade Conversion',
    path: '/conversion-rules/grade-conversions/new',
    icon: DocumentSyncOutlineIcon,
    page: <GradeConversionCreatePage />,
    hidden: true,
  },
];

export const reactRouterV6Routes = routes.map((route) => convertToReactV6(route));

export function useMatches() {
  const matches = baseUseMatches();
  return matches.filter((m) => m.handle).map((m) => m.handle as Route);
}

function convertToReactV6(route: Route): RouteObject {
  if (route.children && route.children.length) {
    // nestedLayout: children render inside this route's layout via <Outlet />
    if (route.nestedLayout) {
      return {
        path: route.path,
        handle: route,
        element: route.page,
        errorElement: <ErrorElement />,
        children: route.children.map((childRoute) => convertToReactV6(childRoute)),
      };
    }

    // Default: children render as separate pages, replacing the parent
    return {
      path: route.path,
      handle: route,
      children: [
        {
          path: '',
          index: true,
          element: route.page,
          errorElement: <ErrorElement />,
        },
        ...route.children.map((childRoute) => convertToReactV6(childRoute)),
      ],
    };
  } else {
    return {
      handle: route,
      path: route.path,
      element: route.page,
      errorElement: <ErrorElement />,
      children: route.children?.map((childRoute) => convertToReactV6(childRoute)),
    };
  }
}
